import React, { useEffect, useRef, useState } from 'react';
import { useIsMobile } from '../../hooks/useMediaQuery';

/**
 * Mobile bottom sheet component
 * Swipeable modal that slides up from bottom
 */
function BottomSheet({ 
  isOpen, 
  onClose, 
  children, 
  title,
  height = 'auto',
  maxHeight = '90vh',
}) {
  const isMobile = useIsMobile();
  const sheetRef = useRef(null);
  const [touchStart, setTouchStart] = useState(null);
  const [touchCurrent, setTouchCurrent] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isMobile) {
    return null;
  }

  const handleTouchStart = (e) => {
    setTouchStart(e.touches[0].clientY);
    setIsDragging(true);
  };

  const handleTouchMove = (e) => {
    if (!touchStart) return;
    setTouchCurrent(e.touches[0].clientY);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchCurrent) {
      setIsDragging(false);
      setTouchStart(null);
      setTouchCurrent(null);
      return;
    }

    const diff = touchCurrent - touchStart;
    
    if (diff > 100) {
      onClose();
    }

    setIsDragging(false);
    setTouchStart(null);
    setTouchCurrent(null);
  };

  if (!isOpen) return null;

  const dragOffset = isDragging && touchStart && touchCurrent 
    ? Math.max(0, touchCurrent - touchStart) 
    : 0;

  return (
    <div className="fixed inset-0 overlay-root z-[110]">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        data-interactive="true"
        onClick={onClose}
        style={{
          opacity: isDragging ? Math.max(0.3, 1 - dragOffset / 300) : 1,
        }}
      />

      {/* Bottom sheet */}
      <div
        ref={sheetRef}
        className="fixed inset-x-0 bottom-0 bg-white dark:bg-gray-800 rounded-t-3xl shadow-2xl transition-transform"
        data-interactive="true"
        style={{
          height: height,
          maxHeight: maxHeight,
          transform: `translateY(${dragOffset}px)`,
          paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)',
        }}
      >
        {/* Drag handle */}
        <div
          className="flex justify-center pt-3 pb-2 cursor-grab active:cursor-grabbing"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className="w-12 h-1.5 bg-gray-300 dark:bg-gray-600 rounded-full" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-3 border-b border-gray-200 dark:border-gray-700">
          {title && (
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {title}
            </h2>
          )}
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition p-2 -mr-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
            aria-label="Close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto" style={{ maxHeight: 'calc(90vh - 100px)' }}>
          {children}
        </div>
      </div>
    </div>
  );
}

export default BottomSheet;
